import dayjs from 'dayjs'
import { WalletTransaction, WalletTransactionSourceType, WalletTransactionStatus } from '@/src/types/wallet'
import { SOURCE_TYPE_LABELS, TRANSACTION_STATUS_LABELS, SOURCES_WITH_REFERENCE } from '@/src/constants/wallet'
import { getFormattedDate } from '@/src/utils/date/dateHelper'

/**
 * Get display label for transaction source type
 * @param sourceType - The source type code
 * @param t - Translation function (optional)
 * @returns Formatted source type label
 */
export const getSourceTypeLabel = (sourceType: string, t?: (key: string) => string): string => {
    if (t) {
        // Map source type codes to translation keys
        const sourceTypeMap: Record<string, string> = {
            F: 'sourceTypes.flight',
            H: 'sourceTypes.hotel',
            FPH: 'sourceTypes.flightsAndHotel',
            FC: 'sourceTypes.flyinCash',
            Qitaf: 'sourceTypes.qitaf',
            WCB: 'sourceTypes.cashback'
        }
        const translationKey = sourceTypeMap[sourceType]
        if (translationKey) {
            return t(translationKey)
        }
    }
    // Fallback to constants if no translation function provided
    return SOURCE_TYPE_LABELS[sourceType as WalletTransactionSourceType] || sourceType
}

/**
 * Get display label for transaction status
 * @param status - The transaction status
 * @param t - Translation function (optional)
 * @returns Formatted status label
 */
export const getStatusLabel = (status: WalletTransactionStatus | undefined, t?: (key: string) => string): string => {
    if (!status) return ''
    if (t) {
        // Normalize status for translation key
        const normalizedStatus = status.toLowerCase().trim()
        if (normalizedStatus === 'active' || normalizedStatus === 'in active') {
            return t('status.active')
        }
        if (normalizedStatus === 'inactive') {
            return t('status.inactive')
        }
    }
    // Fallback to constants if no translation function provided
    return TRANSACTION_STATUS_LABELS[status] || status
}

/**
 * Check if source type should display reference number
 * @param sourceType - The source type code
 * @returns Whether reference number should be shown
 */
export const shouldShowReference = (sourceType: string): boolean => {
    return SOURCES_WITH_REFERENCE.includes(sourceType as WalletTransactionSourceType)
}

/**
 * Format transaction expiry date
 * @param dateStr - ISO date string
 * @returns Formatted date string (DD-MM-YYYY) or '-' if missing
 */
export const formatExpiryDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-'
    try {
        return getFormattedDate(dateStr, 'DD-MM-YYYY')
    } catch {
        return '-'
    }
}

/**
 * Calculate days until expiry date
 * @param expiryDate - Date string in DD-MM-YYYY format
 * @returns Number of days until expiry, or null if invalid/expired
 */
export const getDaysUntilExpiry = (expiryDate: string): number | null => {
    if (!expiryDate || expiryDate === '-') return null
    try {
        const expiry = dayjs(expiryDate, 'DD-MM-YYYY')
        const today = dayjs().startOf('day')
        const daysDiff = expiry.diff(today, 'day')
        return daysDiff >= 0 ? daysDiff : null
    } catch {
        return null
    }
}

/**
 * Format date for display (DD MMM YYYY)
 * @param dateStr - Date string in DD-MM-YYYY format
 * @returns Formatted date string (DD MMM YYYY) or '-' if invalid
 */
export const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr || dateStr === '-') return '-'
    try {
        const date = dayjs(dateStr, 'DD-MM-YYYY')
        if (!date.isValid()) return dateStr
        return date.format('DD MMM YYYY')
    } catch {
        return dateStr
    }
}

/**
 * Format transaction for display
 * @param transaction - The wallet transaction
 * @param t - Translation function (optional)
 * @returns Formatted transaction display object
 */
export const formatTransactionForDisplay = (transaction: WalletTransaction, t?: (key: string) => string) => {
    const sourceLabel = getSourceTypeLabel(transaction.sourceType, t)
    const statusLabel = getStatusLabel(transaction.status, t)

    let displaySource = sourceLabel
    if (transaction.sourceType === 'WCB' && statusLabel) {
        displaySource = `${sourceLabel} (${statusLabel})`
    }

    const showReference = shouldShowReference(transaction.sourceType)
    const referenceDisplay = showReference && transaction.referenceNumber ? `ID: ${transaction.referenceNumber}` : null

    return {
        id: transaction.transactionId,
        source: displaySource,
        reference: referenceDisplay,
        amount: transaction.txnWalletAmt,
        currency: transaction.currency,
        expiryDate: formatExpiryDate(transaction.txnExpireDate),
        transactionDate: formatExpiryDate(transaction.createdAt),
        rawSourceType: transaction.sourceType
    }
}
